import React, {useState} from 'react';
import {AppContext} from "../../HomePage";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import {Dropdown} from "primereact/dropdown";
import {Button} from "primereact/button";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {getKeyValue} from "../../utils/Utils";
import {ADDITIONAL_FEATURE_KEYS} from "./pricingFeatureCatalog";

const BOOLEAN_FIELDS = [
    'product_variant_tracking_enabled',
    'daily_notification_enabled',
    'price_history_enabled',
    'stock_availability_monitoring_enabled',
    'product_cost_monitoring_enabled',
    'instant_notification_enabled',
    'instant_price_check_enabled',
    'sale_history_enabled',
    'competitor_suggestion_enabled',
    'dynamic_pricing_enabled',
    'api_enabled',
];

const SectionTitle = ({icon, text}) => (
    <div className="p-col-12" style={{marginTop: '1rem', marginBottom: '0.25rem'}}>
        <span style={{fontWeight: 600, fontSize: '1rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem'}}>
            <i className={icon} style={{color: 'var(--primary-color, #4f46e5)'}}/> {text}
        </span>
        <hr style={{border: 'none', borderTop: '1px solid var(--surface-border, #e5e7eb)', margin: '0.4rem 0 0'}}/>
    </div>
);

const PricingPlanForm = (props) => {

    const {t} = React.useContext(AppContext);
    const plan = props.plan;

    const CATEGORY_TYPE_OPTIONS = {
        standard: ['basic', 'professional', 'premium', 'trial'],
        shopify: ['paid', 'trial'],
        special: ['special'],
    };
    const deriveCategory = () => plan.user_id ? 'special' : (plan.platform === 'shopify' ? 'shopify' : 'standard');
    const category = props.category || deriveCategory();
    let typeValues = CATEGORY_TYPE_OPTIONS[category] || CATEGORY_TYPE_OPTIONS.standard;
    if (plan.type && !typeValues.includes(plan.type)) typeValues = [...typeValues, plan.type];
    const typeOptions = typeValues.map(v => ({value: v, label: v.charAt(0).toUpperCase() + v.slice(1)}));

    // version counter to force re-render after in-place mutations of `plan`
    const [version, setVersion] = useState(0);
    const refresh = () => setVersion(v => v + 1);
    const [newFeatureName, setNewFeatureName] = useState(null);
    const [newFeaturePrice, setNewFeaturePrice] = useState(0);

    const onChange = (e) => {
        const {key, value} = getKeyValue(e);
        plan[key] = value;
        refresh();
    };

    const onNumberChange = (key, value) => {
        plan[key] = value;
        refresh();
    };

    const onSwitchChange = (key, value) => {
        plan[key] = value;
        refresh();
    };

    const featureNameTemplate = (rowData) => t('subscription.' + rowData.name);

    const featurePriceEditor = (rowData) => {
        return <InputNumber value={rowData.price} style={{width: "120px"}} mode="decimal" minFractionDigits={2}
                            onValueChange={(e) => {
                                rowData.price = getKeyValue(e).value;
                                refresh();
                            }}/>;
    };

    const removeFeatureTemplate = (rowData) => {
        return <Button icon="pi pi-trash" className="p-button-text p-button-danger" type="button"
                       onClick={() => {
                           plan.additional_features = (plan.additional_features || []).filter(f => f.name !== rowData.name);
                           refresh();
                       }}/>;
    };

    const availableFeatureOptions = ADDITIONAL_FEATURE_KEYS
        .filter(key => !(plan.additional_features || []).some(f => f.name === key))
        .map(key => ({value: key, label: t('subscription.' + key)}));

    const addFeature = () => {
        if (!newFeatureName) return;
        if (!plan.additional_features) plan.additional_features = [];
        plan.additional_features.push({name: newFeatureName, price: newFeaturePrice || 0, is_active: true});
        setNewFeatureName(null);
        setNewFeaturePrice(0);
        refresh();
    };

    return <div className="p-fluid">
        <div className="p-grid p-fluid">

            <SectionTitle icon="pi pi-info-circle" text={t('pricing_plan.section_info')}/>
            <div className="p-col-12 p-md-6">
                <label htmlFor="name">{t('subscription.plan')}</label>
                <InputText id="name" name="name" value={plan.name ?? ""} onChange={onChange}/>
            </div>
            <div className="p-col-12 p-md-6">
                <label htmlFor="type">{t('pricing_plan.plan_type')}</label>
                <Dropdown inputId="type" value={plan.type ?? null} options={typeOptions}
                          onChange={(e) => { plan.type = e.value; refresh(); }}/>
            </div>
            <div className="p-col-12 p-md-6">
                <label htmlFor="monthly_price">{t('subscription.monthly_price')}</label>
                <InputNumber id="monthly_price" value={plan.monthly_price ?? null} mode="decimal" minFractionDigits={2} onValueChange={(e) => onNumberChange('monthly_price', e.value)}/>
            </div>
            <div className="p-col-12 p-md-6">
                <label htmlFor="annual_price">{t('subscription.annual_price')}</label>
                <InputNumber id="annual_price" value={plan.annual_price ?? null} mode="decimal" minFractionDigits={2} onValueChange={(e) => onNumberChange('annual_price', e.value)}/>
            </div>

            <SectionTitle icon="pi pi-sliders-h" text={t('pricing_plan.section_limits')}/>
            <div className="p-col-12 p-md-6">
                <label htmlFor="total_product_count">{t('subscription.total_product_count')}</label>
                <InputNumber id="total_product_count" value={plan.total_product_count ?? null} onValueChange={(e) => onNumberChange('total_product_count', e.value)}/>
            </div>
            <div className="p-col-12 p-md-6">
                <label htmlFor="total_competitor_count">{t('subscription.total_competitor_count')}</label>
                <InputNumber id="total_competitor_count" value={plan.total_competitor_count ?? null} onValueChange={(e) => onNumberChange('total_competitor_count', e.value)}/>
            </div>
            <div className="p-col-12 p-md-6">
                <label htmlFor="daily_price_check_count">{t('subscription.daily_price_check_count')}</label>
                <InputNumber id="daily_price_check_count" value={plan.daily_price_check_count ?? null} onValueChange={(e) => onNumberChange('daily_price_check_count', e.value)}/>
            </div>
            <div className="p-col-12 p-md-6">
                <label htmlFor="manuel_price_check_count">{t('subscription.manuel_price_check_count')}</label>
                <InputNumber id="manuel_price_check_count" value={plan.manuel_price_check_count ?? null} onValueChange={(e) => onNumberChange('manuel_price_check_count', e.value)}/>
            </div>

            <SectionTitle icon="pi pi-check-square" text={t('pricing_plan.section_features')}/>
            {BOOLEAN_FIELDS.map(field =>
                <div className="p-col-12 p-md-6" key={field}>
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                 padding: '0.5rem 0.75rem', border: '1px solid var(--surface-border, #e5e7eb)',
                                 borderRadius: '8px'}}>
                        <label htmlFor={field} style={{margin: 0}}>{t('subscription.' + field)}</label>
                        <InputSwitch inputId={field} checked={plan[field] === true}
                                     onChange={(e) => onSwitchChange(field, e.value)}/>
                    </div>
                </div>
            )}

            <SectionTitle icon="pi pi-plus-circle" text={t('pricing_plan.section_addons')}/>
            <div className="p-col-12">
                <DataTable value={plan.additional_features || []} emptyMessage={t('actions.empty_message')}>
                    <Column field="name" header={t('table_headers.name')} body={featureNameTemplate}/>
                    <Column field="price" header={t('subscription.price')} body={featurePriceEditor}/>
                    <Column body={removeFeatureTemplate} headerStyle={{width: '4em'}}/>
                </DataTable>
            </div>
            <div className="p-col-12">
                <div style={{display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap'}}>
                    <div style={{flex: '2 1 200px'}}>
                        <label htmlFor="newFeatureName">{t('pricing_plan.select_feature')}</label>
                        <Dropdown inputId="newFeatureName" value={newFeatureName} options={availableFeatureOptions}
                                  onChange={(e) => setNewFeatureName(e.value)}
                                  placeholder={t('pricing_plan.select_feature')} filter style={{width: '100%'}}/>
                    </div>
                    <div style={{flex: '1 1 120px'}}>
                        <label htmlFor="newFeaturePrice">{t('subscription.price')}</label>
                        <InputNumber inputId="newFeaturePrice" value={newFeaturePrice} mode="decimal" minFractionDigits={2}
                                     onValueChange={(e) => setNewFeaturePrice(e.value)} style={{width: '100%'}}/>
                    </div>
                    <div style={{flex: '0 0 auto'}}>
                        <Button label={t('pricing_plan.add_feature')} icon="pi pi-plus" className="p-button-outlined"
                                type="button" disabled={!newFeatureName} onClick={addFeature}/>
                    </div>
                </div>
            </div>

        </div>
    </div>;
};

export default PricingPlanForm;
